import { FlightSearch } from 'ts/flight/interfaces/FlightSearch';

export const FLIGHT_CARD_ACTION = 'flight-card-action';

export const FLIGHT_CARD_ACTIONS = ['add-to-proposal', 'select', 'info'] as const;

export type FlightCardActionKind = (typeof FLIGHT_CARD_ACTIONS)[number];

export interface FlightCardActionPayload {
  action: FlightCardActionKind;
  index?: number | null;
  flightId?: string | null;
  flightKey?: string | null;
  flightName?: string | null;
  added?: boolean | null;
  show?: boolean | null;
}

const ACTION_ALIASES: Record<string, FlightCardActionKind> = {
  proposal: 'add-to-proposal',
  'add-proposal': 'add-to-proposal',
  heart: 'add-to-proposal',
  select: 'select',
  arrow: 'select',
  '->': 'select',
  continue: 'select',
  choose: 'select',
  go: 'select',
  details: 'info',
  info: 'info',
  'flight-info': 'info',
  segments: 'info',
};

export const isFlightCardAction = (
  action: string | null | undefined,
): action is FlightCardActionKind => {
  if (!action) {
    return false;
  }
  const normalized = ACTION_ALIASES[action] || action;
  return (FLIGHT_CARD_ACTIONS as readonly string[]).includes(normalized);
};

export const parseFlightCardActionPayload = (
  raw: Record<string, unknown> | null | undefined,
): FlightCardActionPayload | null => {
  if (!raw || typeof raw.action !== 'string') {
    return null;
  }

  const action = (ACTION_ALIASES[raw.action] || raw.action) as FlightCardActionKind;
  if (!isFlightCardAction(action)) {
    return null;
  }

  const index =
    typeof raw.index === 'number' && Number.isFinite(raw.index)
      ? Math.floor(raw.index)
      : typeof raw.index === 'string' && raw.index.trim()
        ? Number.parseInt(raw.index, 10)
        : null;

  return {
    action,
    index: index && Number.isFinite(index) && index >= 1 ? index : null,
    flightId:
      typeof raw.flightId === 'string'
        ? raw.flightId
        : typeof raw.id === 'string'
          ? raw.id
          : null,
    flightKey:
      typeof raw.flightKey === 'string'
        ? raw.flightKey
        : typeof raw.uKey === 'string'
          ? raw.uKey
          : null,
    flightName:
      typeof raw.flightName === 'string'
        ? raw.flightName
        : typeof raw.name === 'string'
          ? raw.name
          : null,
    added: typeof raw.added === 'boolean' ? raw.added : null,
    show: typeof raw.show === 'boolean' ? raw.show : null,
  };
};

export const flightCardLabel = (flight: FlightSearch): string => {
  const airline = flight.airlineDesc || flight.airlineCode || '';
  const number = flight.outbound?.gdsFlyAirs?.[0]?.flightNumber || '';
  const route = flight.outbound
    ? `${flight.outbound.departureAirport?.code || ''}-${flight.outbound.arrivalAirport?.code || ''}`
    : '';
  return [airline, number, route].filter(Boolean).join(' ').trim() || flight.id;
};

export const findFlightForCardAction = (
  flights: FlightSearch[],
  payload: FlightCardActionPayload,
): FlightSearch | null => {
  const visible = flights.filter((flight) => flight.visible !== false);

  if (payload.flightId) {
    const byId =
      visible.find((flight) => flight.id === payload.flightId) ||
      flights.find((flight) => flight.id === payload.flightId);
    if (byId) {
      return byId;
    }
  }

  if (payload.flightKey) {
    const byKey =
      visible.find((flight) => flight.uKey === payload.flightKey) ||
      flights.find((flight) => flight.uKey === payload.flightKey);
    if (byKey) {
      return byKey;
    }
  }

  if (payload.index && payload.index >= 1) {
    return visible[payload.index - 1] || null;
  }

  if (payload.flightName) {
    const needle = payload.flightName.trim().toLowerCase();
    return (
      visible.find((flight) => {
        const label = flightCardLabel(flight).toLowerCase();
        return (
          label === needle ||
          label.includes(needle) ||
          (flight.airlineDesc || '').toLowerCase().includes(needle) ||
          (flight.airlineCode || '').toLowerCase() === needle
        );
      }) || null
    );
  }

  return null;
};

export const scrollFlightCardIntoView = (flightId: string) => {
  const card = document.getElementById(`flight-card-${flightId}`);
  if (!card) {
    return;
  }
  card.scrollIntoView({ behavior: 'smooth', block: 'center' });
};

export const cardActionNeedsDom = (action: FlightCardActionKind) => action === 'info';
